import merge from 'lodash/merge';
import random from 'lodash/random';
import {useMemo} from 'react';
import {SchemeLink} from '../../../applink/SchemeLink';
import {t} from '../../../common/getTranslations';
import {
  getStaticAssetUrl,
  StaticAssetType,
} from '../../../common/shared/utils/getStaticAssetUrl';
import {twMerge} from '../../../common/twMerge';
import {useIsDarkMode} from '../../../common/useIsDarkMode.hook';

import {Text} from '../../atoms/Text/Text';
import {ThumbnailRatio} from '../../molecules/ThumbnailWithBadges/ThumbnailRatio.constant';
import {ThumbnailType} from '../../molecules/ThumbnailWithBadges/ThumbnailType.enum';
import {
  FIXED_META_INFO_RATIO,
  FIXED_POSTER_VIEW_WIDTH_PX,
} from '../PosterViewItem/constants';
import {getPosterViewMetaBoxHeight} from '../PosterViewItem/getPosterViewMetaBoxHeight.function';
import {CardViewMoreItemProps} from './CardViewMoreItemProps.interface';

export function CardViewMoreItem({
  productCount,
  title,
  isPosterView,
  isFixedSizePosterView,
  scheme,
  className,
  style,
  ...props
}: CardViewMoreItemProps) {
  const isDarkMode = useIsDarkMode();

  const backgroundImage = useMemo(() => {
    const randomIdx = random(0, 4);
    return BACKGROUND_LIST[randomIdx];
  }, []);

  const paddingBottom = useMemo(() => {
    // 포스터뷰가 아니면 카드뷰 비율 그대로 보여준다.
    if (!isPosterView) {
      return `${CARD_VIEW_RATIO * 100}%`;
    }
    const posterThumbnailHeight = `${
      ThumbnailRatio[ThumbnailType.Poster] * 100
    }%`;
    // 고정 포스터뷰인 경우 고정 비율, 그 외는 계산된 메타박스 영역 높이를 사용
    const posterViewMetaBoxHeight = isFixedSizePosterView
      ? `${FIXED_META_INFO_RATIO * 100}%`
      : getPosterViewMetaBoxHeight();
    // 포스터뷰 썸네일높이 + 하단 메타박스 높이
    return `calc(${posterThumbnailHeight} + ${posterViewMetaBoxHeight});`;
  }, [isPosterView, isFixedSizePosterView]);

  return (
    <SchemeLink scheme={scheme || undefined}>
      <div
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={twMerge(
          'card-view-more-item-container',
          isPosterView && isFixedSizePosterView && 'fixed-card-view-width',
          'relative overflow-hidden rounded-8pxr bg-cover bg-no-repeat',
          className
        )}
        style={merge(
          {
            backgroundImage: isDarkMode
              ? `url(${backgroundImage.dark})`
              : `url(${backgroundImage.light})`,
          },
          style
        )}
        {...props}
      >
        <div className="absolute top-0 left-0 flex h-full w-full flex-col items-center justify-center">
          {((productCount !== undefined && productCount >= 0) || title) && (
            <div className="mb-10pxr text-center">
              {productCount !== undefined && productCount >= 0 && (
                <Text className="font-medium2 break-all text-[rgb(var(--colors-static-mono-white)_/_40%)] opacity-60 line-clamp-1">
                  {Math.floor(productCount)}개
                </Text>
              )}
              {title && (
                <Text className="font-small2 break-all text-[rgb(var(--colors-static-mono-white)_/_40%)] opacity-60 line-clamp-1">
                  {title}
                </Text>
              )}
            </div>
          )}
          <Text className="font-small1 text-static-mono-10">
            {t('common_more_button')}
          </Text>
        </div>
      </div>
      <style jsx>
        {`
          .card-view-more-item-container::before {
            display: block;
            content: '';
            padding-bottom: ${paddingBottom};
          }
          .fixed-card-view-width {
            width: ${FIXED_POSTER_VIEW_WIDTH_PX}px !important;
          }
        `}
      </style>
    </SchemeLink>
  );
}

const BACKGROUND_LIST = [
  {
    dark: getStaticAssetUrl('more_bg_01.png', StaticAssetType.Background),
    light: getStaticAssetUrl(
      'more_bg_01_light.png',
      StaticAssetType.Background
    ),
  },
  {
    dark: getStaticAssetUrl('more_bg_02.png', StaticAssetType.Background),
    light: getStaticAssetUrl(
      'more_bg_02_light.png',
      StaticAssetType.Background
    ),
  },
  {
    dark: getStaticAssetUrl('more_bg_03.png', StaticAssetType.Background),
    light: getStaticAssetUrl(
      'more_bg_03_light.png',
      StaticAssetType.Background
    ),
  },
  {
    dark: getStaticAssetUrl('more_bg_04.png', StaticAssetType.Background),
    light: getStaticAssetUrl(
      'more_bg_04_light.png',
      StaticAssetType.Background
    ),
  },
  {
    dark: getStaticAssetUrl('more_bg_05.png', StaticAssetType.Background),
    light: getStaticAssetUrl(
      'more_bg_05_light.png',
      StaticAssetType.Background
    ),
  },
];

const CARD_VIEW_RATIO = 2 / 0.95;
